import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Space, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';

interface ResourceItem {
  id: string;
  title: string;
  description: string;
  link: string;
  icon: string;
  sort_order?: number;
}

interface ResourcesEditorProps {
  data: ResourceItem[];
  onSave: (data: ResourceItem[]) => void;
}

const ICON_OPTIONS = [
  { label: '计算器', value: 'CalculatorOutlined' },
  { label: '机器人/AI', value: 'RobotOutlined' },
  { label: '折线图', value: 'LineChartOutlined' },
  { label: '药盒', value: 'MedicineBoxOutlined' },
  { label: '实验/科研', value: 'ExperimentOutlined' },
  { label: '团队', value: 'TeamOutlined' },
  { label: '安全认证', value: 'SafetyCertificateOutlined' },
  { label: '闪电', value: 'ThunderboltOutlined' },
  { label: '全球', value: 'GlobalOutlined' },
  { label: '文档', value: 'FileTextOutlined' },
  { label: 'API', value: 'ApiOutlined' },
  { label: '书籍', value: 'BookOutlined' },
];

const API_BASE = process.env.REACT_APP_API_URL || '';

/** 将相对路径或完整 URL 转为可访问的完整 URL */
const resolveLink = (link: string): string => {
  if (!link) return '#';
  if (link.startsWith('http://') || link.startsWith('https://')) return link;
  if (link.startsWith('/')) return `${API_BASE}${link}`;
  return link;
};

const ResourcesEditor: React.FC<ResourcesEditorProps> = ({ data, onSave }) => {
  const [items, setItems] = useState<ResourceItem[]>(data || []);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    setItems(data || []);
  }, [data]);

  const doSave = (newItems: ResourceItem[]) => {
    setItems(newItems);
    onSave(newItems);
  };

  const handleMove = (index: number, direction: 'up' | 'down') => {
    const newItems = [...items];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newItems.length) return;
    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
    doSave(newItems.map((item, i) => ({ ...item, sort_order: i + 1 })));
  };

  const columns = [
    { title: '图标', dataIndex: 'icon', key: 'icon', width: 100 },
    { title: '标题', dataIndex: 'title', key: 'title' },
    {
      title: '链接', dataIndex: 'link', key: 'link', width: 180,
      render: (text: string) => (
        <a href={resolveLink(text)} target="_blank" rel="noopener noreferrer">
          {text?.substring(0, 35) || '—'}
        </a>
      ),
    },
    {
      title: '操作', key: 'action', width: 200,
      render: (_: any, record: ResourceItem, index: number) => (
        <Space>
          <Button size="small" icon={<ArrowUpOutlined />} disabled={index === 0}
            onClick={() => handleMove(index, 'up')} />
          <Button size="small" icon={<ArrowDownOutlined />} disabled={index === items.length - 1}
            onClick={() => handleMove(index, 'down')} />
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(index)}>编辑</Button>
          <Popconfirm title="确定删除吗？" onConfirm={() => handleDelete(index)} okText="确定" cancelText="取消">
            <Button type="link" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleAdd = () => {
    setEditingIndex(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    const item = items[index];
    form.setFieldsValue({ id: item.id, title: item.title, description: item.description, link: item.link, icon: item.icon });
    setModalVisible(true);
  };

  const handleDelete = (index: number) => {
    doSave(items.filter((_, i) => i !== index));
    message.success('删除成功');
  };

  const handleModalOk = () => {
    form.validateFields().then((values) => {
      const newItem: ResourceItem = {
        id: values.id || `resource-${Date.now()}`,
        title: values.title,
        description: values.description,
        link: values.link,
        icon: values.icon,
        sort_order: editingIndex !== null ? items[editingIndex].sort_order : items.length + 1,
      };
      let newItems: ResourceItem[];
      if (editingIndex !== null) {
        newItems = [...items];
        newItems[editingIndex] = newItem;
      } else {
        newItems = [...items, newItem];
      }
      doSave(newItems);
      setModalVisible(false);
      message.success(editingIndex !== null ? '更新成功' : '添加成功');
    });
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>添加资源链接</Button>
      </div>
      <Table columns={columns} dataSource={items} rowKey="id" pagination={false} />
      <Modal
        title={editingIndex !== null ? '编辑资源链接' : '添加资源链接'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        width={700}
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item label="ID" name="id"
            rules={[{ required: editingIndex !== null, message: '请输入 ID' }]}>
            <Input placeholder="自动生成" disabled={editingIndex === null} />
          </Form.Item>
          <Form.Item label="图标" name="icon" rules={[{ required: true, message: '请选择图标' }]}>
            <Select placeholder="请选择图标" options={ICON_OPTIONS} />
          </Form.Item>
          <Form.Item label="标题" name="title" rules={[{ required: true, message: '请输入标题' }]}>
            <Input placeholder="例如：用户手册" />
          </Form.Item>
          <Form.Item label="描述" name="description" rules={[{ required: true, message: '请输入描述' }]}>
            <Input.TextArea placeholder="支持 Markdown 格式" rows={4} />
          </Form.Item>
          <Form.Item label="链接（支持完整 URL 或相对路径如 /api）" name="link"
            rules={[{ required: true, message: '请输入链接' }]}>
            <Input placeholder="https://docs.example.com 或 /api" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ResourcesEditor;
